import { useEffect, useRef } from "preact/hooks";
import { useSignal } from "@preact/signals";
import { useBreakpoint } from "~/hooks";
import { UseSwipePanelParams } from "./interfaces";

const DEFAULT_SWIPE_THRESHOLD = 80;
const DEFAULT_SIDE_THRESHOLD = 60;
const HEADER_HEIGHT = 50;
const DIRECTION_SENSITIVITY = 4;

const useSwipePanel = ({
    to,
    active,
    swipeThreshold = DEFAULT_SWIPE_THRESHOLD,
    sideThreshold = DEFAULT_SIDE_THRESHOLD,
    onSwipe,
    onSwipeBack,
    disabled,
}: UseSwipePanelParams) => {
    const shiftX = useSignal(0);
    const startX = useRef(0);
    const currentX = useRef(0);
    const direction = useRef(0);
    const bpMd = useBreakpoint("md");

    useEffect(() => {
        if (bpMd.value || disabled) {
            return;
        }

        const handleTouchStart = (e: TouchEvent) => {
            if (e.touches.length !== 1) {
                return;
            }

            const content =
                document.querySelector<HTMLDivElement>("[data-content]");
            const isActive = active.peek();

            if (content?.getAttribute("aria-hidden") === "true" && !isActive) {
                return;
            }

            const touch = e.touches[0];

            if (touch.clientY <= HEADER_HEIGHT) {
                return;
            }

            const notLeft = touch.clientX > sideThreshold;
            const notRight = touch.clientX < window.innerWidth - sideThreshold;

            if (
                (isActive && to === "right" && notRight) ||
                (isActive && to === "left" && notLeft) ||
                (!isActive && to === "right" && notLeft) ||
                (!isActive && to === "left" && notRight)
            ) {
                return;
            }

            direction.current = 0;
            startX.current = touch.clientX;
            currentX.current = touch.clientX;
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (startX.current === 0) {
                return;
            }

            if (e.touches.length !== 1) {
                return;
            }

            const touch = e.touches[0];
            const delta = touch.clientX - currentX.current;

            if (Math.abs(delta) >= DIRECTION_SENSITIVITY) {
                direction.current = touch.clientX - currentX.current;
                currentX.current = touch.clientX;
            }

            shiftX.value = touch.clientX - startX.current;
        };

        const handleTouchEnd = () => {
            const isActive = active.peek();
            const thresholdPassed = Math.abs(shiftX.value) > swipeThreshold;
            const isCorrectDirection =
                to === "right" ? direction.current > 0 : direction.current < 0;

            if (!isActive && thresholdPassed && isCorrectDirection) {
                onSwipe();
            }

            if (isActive && thresholdPassed && !isCorrectDirection) {
                onSwipeBack();
            }

            direction.current = 0;
            shiftX.value = 0;
            startX.current = 0;
        };

        const handleTouchCancel = () => {
            direction.current = 0;
            shiftX.value = 0;
            startX.current = 0;
        };

        window.addEventListener("touchstart", handleTouchStart);
        window.addEventListener("touchmove", handleTouchMove);
        window.addEventListener("touchend", handleTouchEnd);
        window.addEventListener("touchcancel", handleTouchCancel);

        return () => {
            window.removeEventListener("touchstart", handleTouchStart);
            window.removeEventListener("touchmove", handleTouchMove);
            window.removeEventListener("touchend", handleTouchEnd);
            window.removeEventListener("touchcancel", handleTouchCancel);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bpMd.value, disabled]);

    return shiftX;
};

export default useSwipePanel;
